<template>
    <div class="flex">
        <NavigationBar />
        <div class="min-h-screen w-full bg-[#f7f9fc] py-10 sm:py-14 px-4 sm:px-6 lg:px-8 font-sans text-gray-800 relative">
            <div class="pointer-events-none absolute -top-24 right-0 w-72 h-72 rounded-full bg-blue-100/60 blur-3xl" />
            <div class="pointer-events-none absolute bottom-0 left-10 w-80 h-80 rounded-full bg-indigo-100/50 blur-3xl" />

            <div class="max-w-6xl mx-auto relative">
                <header class="mb-10 sm:mb-12 text-center max-w-2xl mx-auto">
                    <p class="text-sm font-medium text-blue-600 mb-2">Подписка Tergum</p>
                    <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                        Выберите тариф под ваши задачи
                    </h1>
                    <p class="text-sm sm:text-base text-gray-500 mt-3 leading-relaxed">
                        Масштабируйте автоматизацию без ограничений по потокам и связкам.
                    </p>
                </header>

                <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <div
                        v-for="index in 3"
                        :key="index"
                        class="rounded-2xl border border-gray-100 bg-white p-6 animate-pulse"
                    >
                        <div class="h-10 w-10 rounded-xl bg-gray-100 mb-4" />
                        <div class="h-6 w-32 bg-gray-100 rounded mb-2" />
                        <div class="h-4 w-24 bg-gray-100 rounded mb-6" />
                        <div class="h-10 w-28 bg-gray-100 rounded mb-6" />
                        <div class="space-y-3">
                            <div v-for="line in 5" :key="line" class="h-4 bg-gray-100 rounded" />
                        </div>
                    </div>
                </div>

                <div
                    v-else-if="!subscriptions.length"
                    class="rounded-2xl border border-dashed border-gray-200 bg-white/80 p-12 text-center"
                >
                    <p class="text-base font-medium text-gray-800">Тарифы временно недоступны</p>
                    <p class="text-sm text-gray-500 mt-2">Попробуйте обновить страницу чуть позже</p>
                </div>

                <div
                    v-else
                    class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 items-stretch pt-4 overflow-visible"
                >
                    <article
                        v-for="plan in subscriptions"
                        :key="plan.id ?? plan.name"
                        class="group relative flex flex-col overflow-visible rounded-2xl border bg-white p-6 sm:p-7 transition-all duration-300"
                        :class="[
                            plan.isRecommended
                                ? 'border-blue-200 shadow-[0_20px_50px_rgba(37,99,235,0.12)] scale-[1.02] z-10'
                                : 'border-gray-100 shadow-[0_2px_16px_rgba(15,23,42,0.04)] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,23,42,0.08)]'
                        ]"
                    >
                        <div
                            v-if="plan.isRecommended"
                            class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-4 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold shadow-md whitespace-nowrap"
                        >
                            Лучший выбор
                        </div>

                        <div
                            class="absolute inset-x-0 top-0 h-24 rounded-t-2xl opacity-70 pointer-events-none"
                            :class="getPlanTheme(plan).headerGradient"
                        />

                        <div class="relative">
                            <div class="flex items-start justify-between gap-3 mb-6">
                                <div class="flex items-center gap-3">
                                    <div
                                        class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm"
                                        :class="getPlanTheme(plan).iconBg"
                                    >
                                        <component
                                            :is="getPlanIcon(plan.name)"
                                            class="size-6"
                                            :class="getPlanTheme(plan).iconColor"
                                        />
                                    </div>
                                    <div>
                                        <h2 class="text-xl font-semibold text-gray-900">{{ plan.name }}</h2>
                                        <p class="text-xs text-gray-500 mt-0.5">
                                            {{ getPlanSubtitle(plan) }}
                                        </p>
                                    </div>
                                </div>

                                <span
                                    v-if="plan.price_per_month === 0"
                                    class="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                                >
                                    Бесплатно
                                </span>
                            </div>

                            <div
                                class="rounded-xl px-4 py-4 mb-6"
                                :class="getPlanTheme(plan).priceBg"
                            >
                                <div class="flex items-end gap-1.5">
                                    <span class="text-4xl font-bold text-gray-900 leading-none">
                                        {{ formatPrice(plan.price_per_month) }}
                                    </span>
                                    <span class="text-sm text-gray-500 pb-1">₽/мес</span>
                                </div>
                                <div class="mt-3 flex flex-wrap gap-2">
                                    <span
                                        class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium"
                                        :class="getPlanTheme(plan).chip"
                                    >
                                        {{ formatPrice(plan.transactions_rate_per_month) }} транз. / мес
                                    </span>
                                    <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-white/80 text-gray-600 border border-gray-100">
                                        +{{ formatPrice(plan.price_per_transaction) }} ₽ сверх плана
                                    </span>
                                </div>
                            </div>

                            <div class="flex-1">
                                <p class="text-sm font-semibold text-gray-900 mb-3">Включено в тариф</p>
                                <ul class="space-y-3">
                                    <li
                                        v-for="feature in plan.features"
                                        :key="feature"
                                        class="flex items-start gap-3 text-sm text-gray-600"
                                    >
                                        <span
                                            class="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                                            :class="getPlanTheme(plan).checkBg"
                                        >
                                            <svg
                                                class="w-3 h-3"
                                                :class="getPlanTheme(plan).checkColor"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                            </svg>
                                        </span>
                                        <span class="leading-snug">{{ feature }}</span>
                                    </li>
                                </ul>
                            </div>

                            <router-link
                                v-if="plan.price_per_month !== 0"
                                :to="`/subscriptions/pricing?plan=${plan.name}`"
                                class="mt-7 w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200"
                                :class="plan.isRecommended
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/20'
                                    : 'bg-gray-900 text-white hover:bg-gray-800'"
                            >
                                Перейти к оплате
                                <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </router-link>

                            <div
                                v-else
                                class="mt-7 w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-xl bg-gray-50 text-gray-500 border border-gray-100"
                            >
                                Стартовый план
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import NavigationBar from '@/components/NavigationBar.vue';
import BaseSubIcon from '@/components/UI/svg/subscriptions/BaseSubIcon.vue';
import PremiumSubIcon from '@/components/UI/svg/subscriptions/PremiumSubIcon.vue';
import StandardSubIcon from '@/components/UI/svg/subscriptions/StandardSubIcon.vue';
import { Api } from '@/service/api';
import { onMounted, ref } from 'vue';

const subscriptions = ref([]);
const isLoading = ref(true);

const planIcons = {
    'Базовый': BaseSubIcon,
    'Стандартный': StandardSubIcon,
    'Премиум': PremiumSubIcon,
};

const planThemes = {
    'Базовый': {
        headerGradient: 'bg-gradient-to-b from-slate-50 to-transparent',
        iconBg: 'bg-slate-100',
        iconColor: 'stroke-slate-700',
        priceBg: 'bg-slate-50 border border-slate-100',
        chip: 'bg-slate-100 text-slate-700',
        checkBg: 'bg-slate-100',
        checkColor: 'text-slate-600',
    },
    'Стандартный': {
        headerGradient: 'bg-gradient-to-b from-blue-50 to-transparent',
        iconBg: 'bg-blue-100',
        iconColor: 'stroke-blue-700',
        priceBg: 'bg-blue-50/80 border border-blue-100',
        chip: 'bg-blue-100 text-blue-700',
        checkBg: 'bg-blue-100',
        checkColor: 'text-blue-600',
    },
    'Премиум': {
        headerGradient: 'bg-gradient-to-b from-violet-50 to-transparent',
        iconBg: 'bg-violet-100',
        iconColor: 'stroke-violet-700',
        priceBg: 'bg-violet-50/80 border border-violet-100',
        chip: 'bg-violet-100 text-violet-700',
        checkBg: 'bg-violet-100',
        checkColor: 'text-violet-600',
    },
};

const defaultTheme = planThemes['Базовый'];

const unlimitedFeatures = [
    'Неограниченное количество потоков',
    'Неограниченное количество связок',
    'Неограниченное количество фильтров',
    'Неограниченное количество файлов',
    'Неограниченное количество шагов в связке',
];

onMounted(loadSubscriptions);

async function loadSubscriptions() {
    isLoading.value = true;

    try {
        const plans = await Api.getSubscriptionPlans();
        subscriptions.value = plans.map(mapPlan);
    } catch (error) {
        console.error('Failed to load subscription plans', error);
        subscriptions.value = [];
    } finally {
        isLoading.value = false;
    }
}

function mapPlan(plan) {
    return {
        ...plan,
        isRecommended: plan.name === 'Стандартный',
        features: buildFeatures(plan),
    };
}

function buildFeatures(plan) {
    return [
        `${formatPrice(plan.transactions_rate_per_month)} транзакций в месяц`,
        ...unlimitedFeatures,
    ];
}

function getPlanIcon(name) {
    return planIcons[name] || BaseSubIcon;
}

function getPlanTheme(plan) {
    return planThemes[plan.name] || defaultTheme;
}

function getPlanSubtitle(plan) {
    if (plan.price_per_month === 0) {
        return 'Для знакомства с сервисом';
    }
    if (plan.isRecommended) {
        return 'Оптимальный баланс цены и объёма';
    }
    if (plan.name === 'Премиум') {
        return 'Для команд с высокой нагрузкой';
    }
    return 'Для старта и небольших проектов';
}

function formatPrice(value) {
    return new Intl.NumberFormat('ru-RU').format(value ?? 0);
}
</script>
